/* eslint-disable ssr-friendly/no-dom-globals-in-constructor */
/* eslint-disable lines-between-class-members */
import debounce from 'lodash/debounce';
import { colorTokens } from '../../../tokens/globals/tokens';
import { colorTokens as coreColorTokens } from '../../../tokens/core/tokens';

import { fullPageCelebrationTokens } from '../../../tokens/components/FullPageCelebration/tokens';

/* eslint-disable no-param-reassign */
export default class Confetti {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  targetParticleFactor = 1;
  particles = <Array<Record<string, any>>>[];
  callback = null;
  lastUpdate = Date.now();
  emitter = true;
  outOfBounds = { left: 500, right: 500, bottom: 50, top: 200 };
  lowestParticle = 0;
  targetFps = 30;
  targetDt = 1000 / this.targetFps;
  timeToCompleteAnimation = 10;
  done = false;
  transparent: boolean;
  pointerEvents: boolean;
  zIndex: string;
  colors: Array<string>;
  startFull: boolean;

  constructor(canvas: HTMLCanvasElement, transparent = false, pointerEvents = true, startFull = false, zIndex = '0') {
    this.canvas = canvas;
    this.transparent = transparent;
    this.pointerEvents = pointerEvents;
    this.zIndex = zIndex;
    this.colors = this.getColors();
    this.startFull = startFull;
  }

  get width() {
    return this.canvas && this.canvas.width;
  }

  get height() {
    return this.canvas && this.canvas.height;
  }

  get targetParticles() {
    return Math.floor((this.width * this.height) / 1800);
  }

  randomFromTo = (from, to) => Math.floor(Math.random() * (to - from + 1) + from);

  getColors = () => {
    // A small holiday easter egg
    const date = new Date();
    const today = `${date.getMonth() + 1}/${date.getDate()}`;

    switch (today) {
      case '3/17': // St. Patrick's Day
        return ['#009e60', '#50c878', '#4cbb17', '#ffd700', '#009e60', '#d7f6ef'];
      case '10/31': // Halloween
        return ['#fe9600', '#ffc501', '#fff2d6', '#77477e', '#ff9100', '#77477e'];
      case '12/25': // Christmas Day
        return ['#ff0000', '#ff7878', '#ffffff', '#74d680', '#378b29', '#ff0000'];
      default:
        return [
          colorTokens.GlobalBrandBlue40,
          colorTokens.GlobalBrandRed40,
          colorTokens.GlobalBrandGreen50,
          colorTokens.GlobalBrandOrange40,
          coreColorTokens.CoreLegacyPinkLight,
          colorTokens.GlobalBrandYellow30,
        ];
    }
  };

  emit = (
    ct = 1,
    lockY = false,
    leftBoundary: number | undefined = undefined,
    rightBoundary: number | undefined = undefined,
  ) => {
    const x = leftBoundary || -this.outOfBounds.left;
    const x2 = rightBoundary || this.width + this.outOfBounds.right;

    this.particles.push(
      ...Array(ct)
        .fill({})
        .map(() => ({
          x: Math.random() * (x2 - x) + x, // x-coordinate
          y: lockY ? Math.random() * this.lowestParticle : Math.random() * -this.outOfBounds.top, // y-coordinate TODO:
          r: this.randomFromTo(5, 25), // radius / confetti size
          color: this.colors[Math.floor(Math.random() * this.colors.length)],

          // Following properties describe how the particle is initially rotated and how fast it spins
          rotationX: Math.random() * 10 * Math.PI,
          rotationXSpeed: Math.random() * 0.1 * (Math.round(Math.random()) * 2 - 1),
          rotationZ: Math.random() * 5 * Math.PI,
          rotationZSpeed: Math.random() * 0.1 * (Math.round(Math.random()) * 2 - 1),
          rotationY: Math.random() * 2 * Math.PI,
          rotationYSpeed: Math.random() * 0.1 * (Math.round(Math.random()) * 5 - 1),
        })),
    );
  };

  loop = async () => {
    while (!this.done) {
      const startTime = Date.now();
      const dt = startTime - this.lastUpdate;
      this.lastUpdate = startTime;

      this.update(dt / 1000);
      // eslint-disable-next-line no-await-in-loop
      await this.draw();
      const drawTime = Date.now() - startTime;

      if (drawTime < this.targetDt) {
        this.targetParticleFactor = Math.min(1, this.targetParticleFactor + 0.01);
      } else {
        this.targetParticleFactor = Math.max(0.2, this.targetParticleFactor - 0.01);
      }
    }
  };

  draw = () => {
    this.ctx.clearRect(0, 0, this.width, this.height);

    this.particles.forEach(p => {
      if (this.ctx === null) {
        return;
      }

      // Drawing the confetti is done by drawing a 2 point line segment.
      // Rotation is determined by modulating the position of those points
      // and the stroke width.

      // See this.update() for the computation. This part is time dependent and
      // only draws the line segment.

      this.ctx.beginPath();
      this.ctx.lineWidth = p.stroke; // Stroke width is modulated by x rotation
      this.ctx.strokeStyle = p.color;

      this.ctx.moveTo(p.x1, p.y1);
      this.ctx.lineTo(p.x2, p.y2);

      this.ctx.stroke(); // Draw it
    });

    return new Promise(requestAnimationFrame);
  };

  age = 0;

  update = dt => {
    // Particles sway back and forth together, this value tracks their position in that cycle.
    this.age += dt;

    const newParticles: Array<Record<string, any>> = [];

    this.particles.forEach(p => {
      // Particles are actually just a wide-stroked line segment between two points to efficiently render rectangles.
      // Here we compute the position of those two points and the width of the line using the rotation
      // position and global movement.

      // Updating base position

      // Rotation speed is used to determine how a particle falls.
      // Particles that spin more, travel faster in X direction, but slower in Y.
      const cumulativeRotation = p.rotationXSpeed + p.rotationYSpeed + p.rotationZSpeed;

      // p.r is used to as in input, so smaller particles fall slower, making them appear farther away.
      // Magic number -4 defines how much influcence the rotation has on vertical movement. Make it bigger for slower

      p.y +=
        Math.max(
          Math.cos(this.age) +
            1 + // Global movement, + 1 so always positive
            p.r * 0.5 + // Increase speed if r is larger by half of r
            cumulativeRotation * -4, // how much does rotation impact fall rate
          1,
        ) *
        20 *
        dt; // downward movement is governed by realtime, calculate how far to move down this frame

      // Magic number 5 defines how much influence the rotation has on horizontal movement.
      // Magic number 10 defines how much all particles sway horizontally, the smaller the # the more they move.
      p.x += Math.sin((this.age + cumulativeRotation * 5) / 10);

      // Increment rotational position for each axis
      p.rotationX += p.rotationXSpeed;
      p.rotationZ += p.rotationZSpeed;
      p.rotationY += p.rotationYSpeed;

      // Compute points of line segment

      // Y rotation is done by setting the distance between the two points as the rot_y_offset.
      const rotYOffset = (p.r / 2) * Math.sin(p.rotationY);

      // X rotation is done by modulating stroke width. Does not go below 1px to avoid flickering.
      p.stroke = p.r * Math.max(Math.sin(p.rotationX) * 0.6, 1);

      // Z rotation computed with trig functions to position points around a circle
      // at a radius defined by the y rotation.
      const deltaX = Math.sin(p.rotationZ) * rotYOffset;
      const deltaY = Math.cos(p.rotationZ) * rotYOffset;
      p.x1 = p.x + deltaX;
      p.y1 = p.y - deltaY;
      p.x2 = p.x - deltaX;
      p.y2 = p.y + deltaY;

      // was this.lowestParticle = Math.max(this.lowestParticle, this.y);
      this.lowestParticle = Math.max(this.lowestParticle, p.y);

      if (
        !(p.x > this.width + this.outOfBounds.right) &&
        !(p.x < -this.outOfBounds.left) &&
        !(p.y > this.height + this.outOfBounds.bottom)
      ) {
        newParticles.push(p);
      }
    });

    this.particles = newParticles;

    if (this.emitter) {
      const particlesToAdd = (this.targetParticles * this.targetParticleFactor) / (this.timeToCompleteAnimation / dt);

      if (particlesToAdd > 1) {
        this.emit(Math.floor(Math.min(particlesToAdd, this.targetParticles / 5)));
      } else if (Math.random() <= particlesToAdd) {
        this.emit();
      }
    }

    if (!this.particles.length && !this.emitter) {
      this.done = true;
    }
  };

  init = () => {
    if (!this.canvas) {
      return;
    }
    // canvas dimensions
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;

    this.canvas.style.display = 'block';
    this.canvas.style.position = 'fixed';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';

    if (this.zIndex) {
      this.canvas.style.zIndex = this.zIndex;
    }

    if (!this.pointerEvents) {
      this.canvas.style.pointerEvents = 'none';
    }

    if (this.transparent) {
      this.canvas.style.backgroundColor = 'transparent';
    } else {
      this.canvas.style.backgroundColor = fullPageCelebrationTokens.CoreBackgroundColor;
    }

    window.addEventListener('resize', debounce(this.handleResize, 200));

    if (this.startFull) {
      this.lowestParticle = this.height;
      this.emit(this.targetParticles, true);
    }

    this.loop();
  };

  start = (force = true) => {
    this.ctx = <CanvasRenderingContext2D>this.canvas.getContext('2d');

    if (force) {
      this.init();
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        this.init();
      });
    }
  };

  stopGracefully = () => {
    this.emitter = false;
  };

  kill = () => {
    this.emitter = false;
    this.done = true;
  };

  handleResize = () => {
    const oldWidth = this.canvas.width;

    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;

    if (oldWidth < this.canvas.width) {
      const pctIncrease = this.canvas.width / oldWidth - 1;
      const particlesToAdd = Math.floor(this.particles.length * pctIncrease);
      this.emit(particlesToAdd, true, oldWidth, this.canvas.width);
    }
  };
}
